import { useState, useEffect, useRef } from "react";
import { randomId } from "@/shared/utils/randomId";
import { useIssueStock } from "../../hooks/useStockMovement";
import { useUnitsOfMeasure } from "@/shared/hooks/useUnitsOfMeasure";
import { useNotification } from "@/shared/hooks/useNotification";
import { inputsApi, type Input } from "../../services/inputsApi";
import type { Requisition } from "../../services/requisitionApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input as TextInput } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PackageCheck, CalendarDays, User } from "lucide-react";
import {
  getRequisitionItemName,
  getRequisitionItemOwner,
  getRequisitionItemReference,
  isCustomerSeedItem,
} from "./requisitionDisplay";

interface IssueRequisitionDialogProps {
  requisition: Requisition | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

interface IssueItemState {
  quantity: number;
  notes: string;
}

export const IssueRequisitionDialog = ({
  requisition,
  open,
  onOpenChange,
  onSuccess,
}: IssueRequisitionDialogProps) => {
  const [movementDate, setMovementDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [globalNotes, setGlobalNotes] = useState("");
  const [itemStates, setItemStates] = useState<Record<number, IssueItemState>>({});
  const [inputsMap, setInputsMap] = useState<Record<string, Input>>({});
  const [loadingInputs, setLoadingInputs] = useState(false);
  const [issuingIdx, setIssuingIdx] = useState<number | null>(null);
  const issueKeys = useRef<Record<number, string>>({});
  // Track items already issued in this session, on top of items that came in
  // already-issued (quantityIssued > 0 from a prior session). Declared above the
  // early-return on `requisition` so the cleanup effect's setter references are
  // never in a temporal dead zone when the effect fires on an empty dialog mount.
  const [issuedLocally, setIssuedLocally] = useState<Set<number>>(new Set());
  const [confirmIdx, setConfirmIdx] = useState<number | null>(null);

  const { data: unitsOfMeasure = [] } = useUnitsOfMeasure();
  const issueStock = useIssueStock();
  const notify = useNotification();

  const unitsMap = unitsOfMeasure.reduce(
    (acc, unit) => {
      acc[unit.name.toLowerCase()] = unit.value;
      acc[unit.abbreviation.toLowerCase()] = unit.value;
      return acc;
    },
    {} as Record<string, number>
  );

  useEffect(() => {
    if (!open) {
      issueKeys.current = {};
      setGlobalNotes("");
      setItemStates({});
      setInputsMap({});
      setMovementDate(new Date().toISOString().split("T")[0]);
      setIssuedLocally(new Set());
      setConfirmIdx(null);
      return;
    }

    if (!requisition?.items?.length) return;

    const fetchInputs = async () => {
      setLoadingInputs(true);
      try {
        const inputIds = [...new Set(requisition.items.map((i) => i.inputId).filter(Boolean))] as string[];
        const inputs = await Promise.all(inputIds.map((id) => inputsApi.getInputById(id)));
        const map = inputs.reduce((acc, input) => {
          acc[input.id] = input;
          return acc;
        }, {} as Record<string, Input>);
        setInputsMap(map);

        const states: Record<number, IssueItemState> = {};
        requisition.items.forEach((item, idx) => {
          states[idx] = { quantity: item.quantityRequested, notes: "" };
        });
        setItemStates(states);
      } catch {
        notify.error("Failed to load item details");
      } finally {
        setLoadingInputs(false);
      }
    };

    fetchInputs();
  }, [open, requisition, notify]);

  if (!requisition) return null;

  const isItemAlreadyIssued = (idx: number) => {
    if (issuedLocally.has(idx)) return true;
    const item = requisition?.items[idx];
    if (!item) return false;
    const issued = item.quantityIssued ?? 0;
    const target = item.quantityApproved ?? item.quantityRequested;
    return issued > 0 && issued >= target;
  };

  const performIssue = (idx: number) => {
    const item = requisition!.items[idx];
    const state = itemStates[idx];
    if (!state || state.quantity <= 0) {
      notify.error("Quantity must be greater than 0");
      return;
    }
    if (isItemAlreadyIssued(idx)) {
      notify.warning("This item has already been issued — cannot issue twice.");
      return;
    }

    const unit = unitsMap[item.unitOfMeasure.toLowerCase()] ?? 1;
    setIssuingIdx(idx);

    issueStock.mutate(
      {
        idempotencyKey: issueKeys.current[idx] ??= randomId(),
        inputId: item.inputId ?? null,
        quantity: state.quantity,
        movementDate: new Date(movementDate).toISOString(),
        requisitionId: requisition!.id,
        requisitionItemId: item.id ?? item.inputId ?? item.seedBatchId ?? "",
        greenhouseId: requisition!.greenhouseId,
        farmId: requisition!.farmId,
        fieldId: requisition!.fieldId,
        notes: state.notes || globalNotes || undefined,
        unit,
      },
      {
        onSuccess: () => {
          delete issueKeys.current[idx];
          const itemName = item.inputId
            ? (inputsMap[item.inputId]?.inputName ?? "item")
            : (item.inputName || item.seedBatchNumber || "item");
          notify.success(
            `Stock issued for ${itemName}`
          );
          setIssuingIdx(null);
          // Mark this item as issued locally so the button disables immediately,
          // even before the requisition list refreshes.
          setIssuedLocally((prev) => {
            const next = new Set(prev);
            next.add(idx);
            return next;
          });
          onSuccess();

          // If every item on the requisition is now issued, close the dialog.
          const allIssued = requisition!.items.every((it, i) => {
            if (i === idx) return true;
            if (issuedLocally.has(i)) return true;
            const issued = it.quantityIssued ?? 0;
            const target = it.quantityApproved ?? it.quantityRequested;
            return issued > 0 && issued >= target;
          });
          if (allIssued) {
            notify.success("All items issued — closing.");
            onOpenChange(false);
          }
        },
        onError: () => {
          // Global MutationCache fallback in queryClient surfaces the detailed
          // envelope toast — don't double-toast with a generic one here.
          setIssuingIdx(null);
        },
      }
    );
  };

  const handleIssueItem = (idx: number) => {
    // Open the confirmation dialog instead of issuing immediately.
    setConfirmIdx(idx);
  };

  const updateItemState = (idx: number, patch: Partial<IssueItemState>) => {
    setItemStates((prev) => ({ ...prev, [idx]: { ...prev[idx], ...patch } }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck className="h-5 w-5 text-primary" />
            Issue Stock
          </DialogTitle>
          <DialogDescription>
            Issue stock for each item in this approved requisition.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="rounded-lg border bg-muted/40 p-3 space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <User className="h-3 w-3" /> Department
              </p>
              <p className="text-sm font-medium truncate">{requisition.departmentName}</p>
            </div>
            <div className="rounded-lg border bg-muted/40 p-3 space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <CalendarDays className="h-3 w-3" /> Required By
              </p>
              <p className="text-sm font-medium">
                {new Date(requisition.requiredByDate).toLocaleDateString()}
              </p>
            </div>
            <div className="rounded-lg border bg-muted/40 p-3 space-y-1">
              <p className="text-xs text-muted-foreground">Items</p>
              <p className="text-sm font-medium">
                {requisition.items.length} item{requisition.items.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          <div className="rounded-lg bg-muted/40 border px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1">Purpose</p>
            <p className="text-sm">{requisition.purpose}</p>
          </div>

          {/* Movement date */}
          <Field>
            <FieldLabel>Issue Date</FieldLabel>
            <DatePicker
              value={movementDate}
              onChange={setMovementDate}
              className="w-48"
            />
          </Field>

          <Separator />

          {/* Items */}
          <div>
            <p className="text-sm font-medium mb-3">Items to Issue</p>
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Input</TableHead>
                    <TableHead className="text-right">Requested</TableHead>
                    <TableHead className="text-right w-32">Issue Qty</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingInputs ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                        Loading items…
                      </TableCell>
                    </TableRow>
                  ) : (
                    requisition.items.map((item, idx) => {
                      const isSeedBatch = isCustomerSeedItem(item);
                      const input = item.inputId ? inputsMap[item.inputId] : null;
                      const displayName = isSeedBatch
                        ? getRequisitionItemName(item)
                        : (input?.inputName ?? item.inputName ?? "Loading…");
                      const displayCode = input?.inputCode
                        ? `Reference ${input.inputCode}`
                        : getRequisitionItemReference(item);
                      const state = itemStates[idx] ?? { quantity: item.quantityRequested, notes: "" };
                      const isIssuing = issuingIdx === idx && issueStock.isPending;
                      const alreadyIssued = isItemAlreadyIssued(idx);
                      return (
                        <TableRow key={idx}>
                          <TableCell>
                            <div className="flex flex-col gap-0.5">
                              <span className="text-sm font-medium">
                                {displayName}
                              </span>
                              {displayCode !== "-" && (
                                <span className="text-xs text-muted-foreground">{displayCode}</span>
                              )}
                              <span className="text-xs text-muted-foreground">Owner: {getRequisitionItemOwner(item)}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {item.quantityRequested}
                          </TableCell>
                          <TableCell className="text-right">
                            <TextInput
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={state.quantity}
                              onChange={(e) =>
                                updateItemState(idx, {
                                  quantity: parseFloat(e.target.value) || 0,
                                })
                              }
                              disabled={isIssuing || alreadyIssued}
                              className="w-24 text-right"
                            />
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {item.unitOfMeasure}
                          </TableCell>
                          <TableCell className="text-right">
                            {alreadyIssued ? (
                              <Badge variant="default">Issued</Badge>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleIssueItem(idx)}
                                disabled={issueStock.isPending || loadingInputs}
                              >
                                {isIssuing ? "Issuing…" : "Issue"}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Global notes */}
          <Field>
            <FieldLabel>Notes <span className="text-muted-foreground font-normal">(applied to all items)</span></FieldLabel>
            <Textarea
              placeholder="Optional notes for this stock issue…"
              value={globalNotes}
              onChange={(e) => setGlobalNotes(e.target.value)}
              rows={2}
            />
          </Field>

          <Separator />
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </div>

        {/* Confirmation step before posting an issue — irreversible stock movement. */}
        <Dialog
          open={confirmIdx !== null}
          onOpenChange={(o) => !o && setConfirmIdx(null)}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm stock issue</DialogTitle>
              <DialogDescription>
                This creates a stock movement that decrements inventory. It can't
                be reversed without raising a return.
              </DialogDescription>
            </DialogHeader>
            {confirmIdx !== null && requisition.items[confirmIdx] && (() => {
              const item = requisition.items[confirmIdx];
              const state = itemStates[confirmIdx];
              const isSeedBatch = isCustomerSeedItem(item);
              const input = item.inputId ? inputsMap[item.inputId] : null;
              const itemName = isSeedBatch
                ? getRequisitionItemName(item)
                : (input?.inputName ?? item.inputName ?? "—");
              return (
                <div className="text-sm rounded border bg-muted/30 p-3 space-y-1">
                  <div>
                    <span className="text-muted-foreground">Item:</span>{" "}
                    <span className="font-medium">{itemName}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Owner:</span>{" "}
                    <span className="font-medium">{getRequisitionItemOwner(item)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Issuing:</span>{" "}
                    <span className="font-medium">
                      {state?.quantity ?? 0} {item.unitOfMeasure}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Requisition:</span>{" "}
                    {requisition.requisitionNumber ?? requisition.id.slice(0, 8)}
                  </div>
                </div>
              );
            })()}
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setConfirmIdx(null)}
                disabled={issueStock.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const idx = confirmIdx;
                  setConfirmIdx(null);
                  if (idx !== null) performIssue(idx);
                }}
                disabled={issueStock.isPending}
              >
                {issueStock.isPending ? "Issuing…" : "Confirm Issue"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};
